<template>
  <v-app id="app">
    <!-- Loading -->
    <div id="app-loading" class="d-flex justify-center align-center">
      <div class="text-xs-center">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <p class="mt-3 mb-5">Loading Elections</p>
      </div>
    </div>

    <!-- App -->
    <template v-if="!loading">
      <forecast-header></forecast-header>
      <template v-if="mobile">
        <top-nav-secondary style="margin-top: 10px;" v-if="$route.name !== 'calculator'"></top-nav-secondary>
        <seat-search v-if="showSearch"></seat-search>
        <date-select v-if="showTimeline"></date-select>
      </template>
      <nav-menu v-if="showMenu"></nav-menu>
      <v-content>
        <v-container id="content-container" :fluid="$route.meta && $route.meta.fluid ? true : false" style="height: 100%; padding-top: 5px; padding-bottom: 0px;">
          <div :style="!mobile ? 'display: flex; height: 100%;' : ''">
            <toolbar id="app-toolbar" style="width: 280px" v-if="!mobile && !$route.path.includes('simulator')"></toolbar>
            <div :style="!$route.path.includes('simulator') ? 'width: calc(100% - 280px);' : 'width: 100%;'" v-if="!mobile">
              <router-view class="app-content" :key="$route.name" style="padding: 0 20px; height: fit-content;'" />
              <forecast-footer></forecast-footer>
            </div>
            <router-view class="app-content" :key="$route.name" v-else />
          </div>
          <!-- <transition name="fade" mode="out-in"> -->
            <!-- <router-view :key="$route.name" /> -->
          <!-- </transition> -->
        </v-container>
      </v-content>

      <forecast-footer v-if="mobile"></forecast-footer>

      <disclaimer></disclaimer>
    </template>
  </v-app>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import ForecastHeader from '@/components/layout/Header'
import ForecastFooter from '@/components/layout/Footer'
import Toolbar from '@/components/layout/Toolbar'
import TopNavSecondary from '@/components/layout/TopNavSecondary'
import Disclaimer from '@/components/Disclaimer'
import { DateSelect, MobileTimelineSlider, NavMenu, SeatSearch, TimelineSlider } from '@/components/common'

const name = 'app'

const computed = {
  ...mapGetters({
    cds: 'geojson/cds',
    dmasByCd: 'geojson/dmasByCd',
    dmasByState: 'geojson/dmasByState',
    states: 'geojson/states',
    mobile: 'ux/mobile',
    mobilePhone: 'ux/mobilePhone',
    screenWidth: 'ux/screenWidth',
    showSearch: 'ux/search',
    showTimeline: 'ux/timeline',
    showMenu: 'ux/menu'
  }),
  isLoginPage () {
    return this.$route.name === 'login'
  }
}

const methods = {
  ...mapActions({
    getAggregateModels: 'aggregateModels/list',
    getCds: 'geojson/getCds',
    getDmasByCd: 'geojson/getDmasByCd',
    getDmasByState: 'geojson/getDmasByState',
    getElections: 'elections/list',
    // getPages: 'pages/list',
    getStates: 'geojson/getStates',
    setScreenWidth: 'ux/setWidth',
    toggleClosed: 'ux/toggleClosed'
  }),
  getWindowSize () {
    if (this.resizeTimeout !== null) {
      clearTimeout(this.resizeTimeout)
    }
    setTimeout(() => {
      this.setScreenWidth(document.documentElement.clientWidth)
      this.resizeTimeout = null
    }, 200)
  }
}

const components = {
  DateSelect,
  ForecastHeader,
  ForecastFooter,
  MobileTimelineSlider,
  NavMenu,
  TopNavSecondary,
  SeatSearch,
  TimelineSlider,
  Toolbar,
  Disclaimer
}

export default {
  name,
  components,
  computed,
  methods,
  metaInfo: {
    titleTemplate: (titleChunk) => {
      return titleChunk ? `${titleChunk} - Øptimus 2018 Election Predictions` : 'Øptimus 2018 Election Predictions'
    },
    meta: [
      {
        charset: 'utf-8'
      }, {
        name: 'article:publisher',
        content: 'https://www.facebook.com/0ptimusPredicts/'
      }, {
        name: 'article:tag',
        content: 'Congress'
      }, {
        name: 'article:tag',
        content: 'Election Day'
      }, {
        name: 'article:tag',
        content: 'US House'
      }, {
        name: 'article:tag',
        content: 'US Senate'
      }, {
        name: 'og:locale',
        content: 'en_US'
      }, {
        name: 'og:type',
        content: 'website'
      }, {
        name: 'og:site_name',
        content: 'Øptimus 2018 Election Predictions'
      }, {
        vmid: 'og:image',
        name: 'og:image',
        content: 'https://s3.us-east-2.amazonaws.com/forecasting.decisiondeskhq.com/open-graph.png'
      }, {
        vmid: 'og:image:secure_url',
        name: 'og:image:secure_url',
        content: 'https://s3.us-east-2.amazonaws.com/forecasting.decisiondeskhq.com/open-graph.png'
      }, {
        name: 'og:image:width',
        content: '1200'
      }, {
        name: 'og:image:height',
        content: '600'
      }, {
        name: 'twitter:card',
        content: 'summary_large_image'
      }, {
        vmid: 'twitter:image',
        name: 'twitter:image',
        content: 'https://s3.us-east-2.amazonaws.com/forecasting.decisiondeskhq.com/open-graph.png'
      }, {
        name: 'twitter:creator',
        content: '@0ptimusPredicts'
      }, {
        vmid: 'og:title',
        name: 'og:title',
        content: 'Home'
      }, {
        vmid: 'twitter:title',
        name: 'twitter:title',
        content: 'Home'
      }, {
        vmid: 'description',
        name: 'description',
        content: '2018 U.S. midterm election predictions for Senate and House Congressional elections. Includes model-driven predictions for key Senate and House races, polling and district information, and model simulations updated weekly.'
      }, {
        vmid: 'og:description',
        name: 'og:description',
        content: '2018 U.S. midterm election predictions for Senate and House Congressional elections. Includes model-driven predictions for key Senate and House races, polling and district information, and model simulations updated weekly.'
      }, {
        vmid: 'twitter:description',
        name: 'twitter:description',
        content: '2018 U.S. midterm election predictions for Senate and House Congressional elections. Includes model-driven predictions for key Senate and House races, polling and district information, and model simulations updated weekly.'
      }
    ]
  },
  data () {
    return {
      loading: false,
      resizeTimeout: null
    }
  },
  beforeMount () {
    this.loading = true

    // clear old keys when topos are updated, find better naming convention
    for (var key in localStorage) {
      if (key.includes('op_forecasting') && key !== 'op_forecasting_topojsons_v1') {
        localStorage.removeItem(key)
      }
    }

    const promises = [
      this.getElections(),
      // this.getPages(),
      this.getAggregateModels()
    ]
    if (this.cds.length === 0) {
      promises.push(this.getCds())
    }
    if (this.dmasByCd.length === 0) {
      promises.push(this.getDmasByCd())
    }
    if (this.dmasByState.length === 0) {
      promises.push(this.getDmasByState())
    }
    if (this.states.length === 0) {
      promises.push(this.getStates())
    }

    if (this.$auth.token) {
      // Authenticate existing token
      promises.push(this.$auth.authenticate())
    }

    Promise.all(promises).then(() => {
      this.loading = false
      if (typeof window.__PRERENDER_INJECTED === 'undefined') {
        const appLoading = document.getElementById('app-loading')
        appLoading.parentNode.removeChild(appLoading)
      }
    }).then(() => {
      if (this.$auth.authenticated === true && this.$route.name === 'login') {
        this.$router.replace('/')
      }
    })
  },
  mounted () {
    this.getWindowSize()
    window.addEventListener('resize', this.getWindowSize)
    window.addEventListener('keydown', (e) => {
      if (e.keyCode === 27 && (this.showSearch || this.showTimeline)) {
        this.toggleClosed()
      }
    })
  },
  watch: {
    $route (to, from) {
      window.scrollTo(0, 160)
    }
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.getWindowSize)
    window.removeEventListener('keydown', (e) => {
      if (e.keyCode === 27 && (this.showSearch || this.showTimeline)) {
        this.toggleClosed()
      }
    })
  }
}
</script>

<style>
.toolbar__content {
  height: 100px !important;
}

.arrow-down {
  position: absolute;
  top: 100px;
  width: 0;
  height: 0;
  border-left: 20px solid transparent;
  border-right: 20px solid transparent;
  border-top: 20px solid #404040;
}

.nav-btn {
  letter-spacing: 0.2em;
}

#content-container .btn--floating {
  position: relative;
}
</style>



// WEBPACK FOOTER //
// src/App.vue